// frontend/src/shared/pricing/constants.js
// PURE CONSTANTS - ZERO DEPENDENCIES

/* The share of the market rate a partner is expected to actually charge.

   One dial, read in two places that must agree: the onboarding form prefills the boxes
   with it, and the fare calculator falls back to it for any box the partner left empty.
   If those two disagreed, a partner who accepted the prefill would be dearer than one
   who typed nothing — the opposite of what either is for.

   Admin-editable as _config.carSuggestedPct on the city rate card; this is only the
   value used when a city has not set one. */
export const DEFAULT_CAR_SUGGESTED_PCT = 80;

export function resolveSuggestedPct(cityPricing) {
  const n = Number(cityPricing?._config?.carSuggestedPct);
  return Number.isFinite(n) && n > 0 && n <= 100 ? n : DEFAULT_CAR_SUGGESTED_PCT;
}

/* Fields that are not prices and must never be discounted when a suggested card is
   seeded from the market one. A minimum-kilometres floor scaled to 80% is not a
   cheaper floor, it is a different floor; fuel efficiency scaled at all is nonsense. */
export const NON_PRICE_FIELDS = new Set([
  "minKms",        // a distance floor - 80% of 320km is a different floor, not a cheaper one
  "fuelKmPerLtr",  // an efficiency figure; scaling it is meaningless
  "maxExtraHrs",   // a count of hours. Seeding at 90% turned every 2 into 1.8 - a
                   // limit quietly cut by a discount that was never meant to reach it.
]);

const positive = (v) => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
};

/**
 * What a partner is expected to charge for one field - the number their onboarding
 * form is prefilled with, and the number a box they left empty is quoted at.
 *
 * These two must be the same number. They always were, when both derived it from one
 * percentage; they still are, because both now read this.
 *
 * The admin sets them explicitly per category and per field, on the Suggested table
 * beside the market one. A city whose suggested card has not been seeded yet - or a
 * single field an admin has not filled - falls back to the old percentage of the
 * market rate, so nothing is unpriced during the changeover.
 */
export function suggestedValue(cityPricing, categoryName, section, field) {
  const explicit = positive(cityPricing?._suggested?.[categoryName]?.[section]?.[field]);
  if (explicit !== null) return explicit;

  const market = positive(cityPricing?.[categoryName]?.[section]?.[field]);
  if (market === null) return null;
  if (NON_PRICE_FIELDS.has(field)) return market;
  return toSuggested(market, resolveSuggestedPct(cityPricing));
}

/**
 * A whole suggested category block, in the market card's shape.
 *
 * Used by the onboarding prefill and by the fare engine, so a form that was accepted
 * as-is and a form left blank produce the same price. Seeded the same way: what is
 * not set explicitly comes from the market rate at the legacy percentage.
 */
export function suggestedCardFor(cityPricing, categoryName) {
  const market = cityPricing?.[categoryName];
  if (!market || typeof market !== "object") return {};
  const out = {};
  for (const [section, values] of Object.entries(market)) {
    if (!values || typeof values !== "object") continue;
    out[section] = {};
    for (const field of Object.keys(values)) {
      const v = suggestedValue(cityPricing, categoryName, section, field);
      if (v !== null) out[section][field] = v;
    }
  }
  return out;
}

/* Market value -> suggested value. Per-km rates keep two decimals because they are
   genuinely fractional (₹11.70); anything ₹100 or over is whole rupees. Same rounding
   the onboarding prefill has always used, so a prefilled box and a blank one resolve to
   the same number rather than differing by a rounding step. */
/* How far from a city's centre a pickup may be and still count as that city.
 *
 * Per city, because the cities differ: Vijayawada's suburbs and satellite towns —
 * Gannavaram at 17km, Nunna at 8km, Mangalagiri at 13km — all sit well outside the
 * municipal boundary but are plainly part of the same travel market, while a smaller
 * town's catchment is much tighter. Admin-set as _config.pickupRadiusKm on the city
 * rate card; this is the value used until a city sets its own.
 *
 * Measured from the city centre Google returns for the city name, which is why nothing
 * here needs a stored coordinate.
 */
/* Two radii, deliberately separate, because they answer different questions.
 *
 * PICKUP radius asks "does this pickup belong to this city?" — measured from the city
 * centre, it decides whether a booking or an onboarding is accepted here at all, and
 * it follows how far the city's own catchment reaches.
 *
 * LISTING radius asks "can this car reach this customer?" — measured from the pickup
 * point to where each car actually sits, it decides which cars are offered. It follows
 * how far a partner will drive to a job, which is a smaller and quite different number.
 *
 * They were one figure once and it fitted neither: widening the catchment to admit a
 * satellite town also offered every car in it to someone across the city.
 */
export const DEFAULT_LISTING_RADIUS_KM = 25;

export function resolveListingRadiusKm(cityPricing) {
  const n = Number(cityPricing?._config?.listingRadiusKm);
  return Number.isFinite(n) && n > 0 ? n : DEFAULT_LISTING_RADIUS_KM;
}

/* The distances a car owner or driver may pick to be seen within (26 Oct 2026; the
   backend's utils/visibleRadius.js holds the same rule): 10, 15, the city's figure, 40,
   50 - up to the most an admin allows (maxBusinessRadiusKm, 50 unless set). */
export function businessRadiusChoices(cityPricing) {
  const base = resolveListingRadiusKm(cityPricing);
  const m = Number(cityPricing?._config?.maxBusinessRadiusKm);
  const max = Math.max(base, Number.isFinite(m) && m > 0 ? m : 50);
  return { base, max, choices: [...new Set([10, 15, base, 40, 50, max])].filter((n) => n <= max).sort((a, b) => a - b) };
}

export const DEFAULT_PICKUP_RADIUS_KM = 40;

export function resolvePickupRadiusKm(cityPricing) {
  const n = Number(cityPricing?._config?.pickupRadiusKm);
  return Number.isFinite(n) && n > 0 ? n : DEFAULT_PICKUP_RADIUS_KM;
}

export function toSuggested(marketVal, pct) {
  const n = Number(marketVal);
  if (!Number.isFinite(n) || n <= 0) return n;
  /* Whole rupees. This used to keep two decimals below 100, on the argument that a
     per-km rate is genuinely fractional - and it produced a card full of 10.8 and
     12.6. Every one of the 584 market figures it derives from is a whole number, so
     the decimals added no accuracy, only a price nobody can quote over a phone.
     Rounded, not floored: 10.8 is 11, and 12.4 is 12. */
  return Math.round((n * pct) / 100);
}

/* One category's suggested block, seeded from its market block at `pct`: every price
   scaled, the distances and fuel figures copied as they are, and a zero left out - the
   suggestion then follows the market, which names none. What Admin > Pricing seeds,
   and the tariff script with it, so the two cannot seed differently. */
export function seedSuggestedBlock(market, pct) {
  const out = {};
  for (const [section, values] of Object.entries(market || {})) {
    if (!values || typeof values !== "object") continue;
    out[section] = {};
    for (const [field, raw] of Object.entries(values)) {
      const n = Number(raw);
      if (!Number.isFinite(n) || n <= 0) continue;
      out[section][field] = NON_PRICE_FIELDS.has(field) ? n : toSuggested(n, pct);
    }
  }
  return out;
}

/* The only fuel values the platform recognises.
 *
 * It was inlined in four places and derived in a fifth, and the derivation joined
 * everything it found with " / " — so a Dzire CNG, which detects as petrol AND CNG,
 * was stored as "Petrol / CNG". That is not one of these four, so nothing downstream
 * could match it: no filter, no badge, no select could show it back.
 *
 * Hybrid and PHEV are deliberately absent. They are petrol cars for every purpose
 * this platform has — Electric means a vehicle that takes no fuel.
 */
export const FUEL_TYPES = ["Petrol", "Diesel", "CNG", "Electric"];

/* One value out of many, when a model could be several.
 *
 * Order matters and is not alphabetical: a bi-fuel car is registered and sold as CNG,
 * and a customer filtering for CNG must find it. Petrol is last because almost
 * everything can also be petrol, so it is the least informative answer.
 */
const FUEL_PRIORITY = ["CNG", "Electric", "Diesel", "Petrol"];

export function normaliseFuelType(value) {
  const v = String(value || "").toLowerCase();
  if (!v) return "";
  if (v.includes("cng")) return "CNG";
  // Checked before "petrol" so a plug-in hybrid does not read as electric, and after
  // CNG so nothing overrides it.
  if (v.includes("electric") || v.includes("ev")) return "Electric";
  if (v.includes("diesel")) return "Diesel";
  if (v.includes("petrol") || v.includes("hybrid")) return "Petrol";
  return FUEL_TYPES.includes(value) ? value : "";
}

export function pickFuelType(list) {
  const found = (Array.isArray(list) ? list : []).map(normaliseFuelType).filter(Boolean);
  for (const f of FUEL_PRIORITY) if (found.includes(f)) return f;
  return "";
}

export const VEHICLE_CATEGORIES = [
  "HATCHBACK_5",
  "SEDAN_5_STD",
  "SEDAN_5_LUX",
  "MINI_SUV_5",
  "SUV_5",
  "SUV_7_ECO",
  "SUV_7_STD",
  "SUV_7_COMFORT",
  "SUV_7_LUX",
  "SUV_7_PREMIUM",
  "TEMPO_TRAVELLER_11",
  "TEMPO_TRAVELLER_12",
  "TEMPO_TRAVELLER_16",
  "MINI_BUS_13",
  "MINI_BUS_23",
  // Above MINI_BUS_23, which now means 20–22 seats only. Appended rather than
  // inserted in seat order: getAssociationVehicleIndex() returns a position in this
  // array, so reordering it would move every existing category.
  "BUS_24",
  "BUS_26",
  "BUS_28",
  "BUS_32"
];

/* Categories an admin has added, on top of the built-in list above.
 *
 * A custom category is not a special case anywhere: it carries the same four rate
 * sections, is quoted by the same calculator, and can be mapped from a model like any
 * other. The only thing it needs is to exist in the list, which is why this is the one
 * mechanism rather than a parallel code path.
 *
 * Appended rather than inserted. getAssociationVehicleIndex returns a position in
 * this array — the value is currently written onto the rates object and read by
 * nothing, but keeping the array stable costs nothing and means that never has to be
 * re-checked. Presentation order is a separate thing entirely; see categoryOrder.
 *
 * Injected rather than fetched, the same as the category rules, so the browser and the
 * server agree without either importing the other's world.
 */
let _customCategories = [];

export function setCustomCategories(list) {
  if (!Array.isArray(list)) return;
  const builtin = new Set(VEHICLE_CATEGORIES);
  const seen = new Set();
  _customCategories = list
    .map((c) => (typeof c === "string" ? { key: c, label: c } : c))
    .filter((c) => c && typeof c.key === "string")
    .map((c) => ({ key: c.key.trim(), label: String(c.label || c.key).trim() }))
    // A custom key that shadows a built-in one would make the array ambiguous and the
    // index meaningless. Dropped rather than merged.
    .filter((c) => c.key && !builtin.has(c.key) && !seen.has(c.key) && seen.add(c.key));
}

export function getCustomCategories() {
  return _customCategories;
}

/** Every category in force — built-in first, custom appended. */
export function allVehicleCategories() {
  return [...VEHICLE_CATEGORIES, ..._customCategories.map((c) => c.key)];
}

/* The order categories are SHOWN in, which is not the order they are stored in.
 *
 * An admin adding "Sedan 5 Premium" wants it beside the other sedans, not at the
 * bottom because that is when it was created. Reordering the canonical array to
 * achieve that would change identity for the sake of presentation, so this is a
 * separate list of keys and nothing but rendering reads it.
 *
 * Anything missing from the list keeps its natural position at the end, so a category
 * added after the order was last saved appears rather than vanishing — the failure
 * that matters here is a category an admin cannot see to price.
 */
let _categoryOrder = [];

export function setCategoryOrder(list) {
  _categoryOrder = Array.isArray(list) ? list.filter((k) => typeof k === "string") : [];
}

export function getCategoryOrder() {
  return _categoryOrder;
}

export function orderCategories(keys, order = _categoryOrder) {
  const present = new Set(keys);
  const ranked = (order || []).filter((k) => present.has(k));
  const rest = keys.filter((k) => !ranked.includes(k));
  return [...ranked, ...rest];
}

/** Every category in force, in the order an admin wants them shown. */
export function orderedVehicleCategories() {
  return orderCategories(allVehicleCategories());
}

/* Names an admin has given categories, built-in ones included.
 *
 * DISPLAY_LABELS is a code constant, so "MPV" or "Mini SUV" could only be changed by
 * a deploy — and those names are what a customer reads on a card. An override here
 * renames any category without touching its key, which is the part records reference
 * and must never move.
 */
let _categoryLabels = {};

export function setCategoryLabels(map) {
  _categoryLabels = map && typeof map === "object" ? map : {};
}

export function getCategoryLabels() {
  return _categoryLabels;
}

export function categoryLabel(key) {
  const named = _categoryLabels[key];
  if (typeof named === "string" && named.trim()) return named.trim();
  const custom = _customCategories.find((c) => c.key === key);
  return custom ? custom.label : null;
}

/* A seat range a keyword rule must also satisfy.
 *
 * "Traveller" alone cannot tell a 13-seater from a 26-seater, and an admin asked for
 * both conditions: the name says which family, the count says which tier. Only
 * applied when the vehicle's seat count is known — with no count there is nothing to
 * test, and refusing the keyword then would leave the car with no category at all
 * rather than an imperfect one.
 */
let _categorySeats = {};

export function setCategorySeats(map) {
  _categorySeats = map && typeof map === "object" ? map : {};
}

export function getCategorySeats() {
  return _categorySeats;
}

export function seatRuleAllows(key, seatNum) {
  const r = _categorySeats[key];
  if (!r) return true;
  if (!Number.isFinite(seatNum) || seatNum <= 0) return true;
  const min = Number(r.min), max = Number(r.max);
  if (Number.isFinite(min) && min > 0 && seatNum < min) return false;
  if (Number.isFinite(max) && max > 0 && seatNum > max) return false;
  return true;
}
